import { Category, MonthlyHabitData, HabitType } from '../types';
import { Todo } from '../types/todo';
import { WheelSection, WheelNoteRecord } from '../types/goals';
import { supabase } from '../lib/supabaseClient';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${session.access_token}` };
}

let pendingDeletionHandler: ((deletionDueAt: string) => void) | null = null;

export function setPendingDeletionHandler(handler: ((deletionDueAt: string) => void) | null) {
  pendingDeletionHandler = handler;
}

async function apiFetch(path: string, options?: RequestInit): Promise<any> {
  const authHeaders = await getAuthHeaders();
  const merged: RequestInit = {
    ...options,
    headers: { ...authHeaders, ...options?.headers },
  };
  const res = await fetch(`${API_BASE}${path}`, merged);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    if (err.details?.code === 'ACCOUNT_PENDING_DELETION') {
      pendingDeletionHandler?.(err.details.deletionDueAt);
    }
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

// ─── Categories ──────────────────────────────────────────────────────────────

export async function fetchCategories(): Promise<Category[]> {
  const data = await apiFetch('/api/categories');
  const cats: any[] = data.categories ?? [];
  const habits: any[] = data.habits ?? [];
  return cats.map(cat => ({
    id: cat.id,
    name: cat.name,
    color: cat.color,
    habits: habits
      .filter(h => h.category_id === cat.id)
      .map(h => ({
        id: h.id,
        name: h.name,
        type: h.type as HabitType,
        category: h.category_id,
        color: h.color,
        ...(typeof h.scheduled_time === 'string'
          ? { scheduledTime: h.scheduled_time.slice(0, 5) }
          : {}),
        ...(h.temporary ? { temporary: true } : {}),
        ...(h.created_at ? { createdAt: h.created_at } : {}),
      })),
  }));
}

export async function syncCategories(categories: Category[]): Promise<void> {
  await apiFetch('/api/categories', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ categories }),
  });
}

export async function deleteHabit(habitId: string): Promise<void> {
  await apiFetch(`/api/habits/${encodeURIComponent(habitId)}`, { method: 'DELETE' });
}

// ─── Entries ─────────────────────────────────────────────────────────────────

/**
 * Fetch all habit entries for a given year.
 * Keyed as `${habitId}-${year}-${month0indexed}` to match the app's MonthlyHabitData shape.
 */
export async function fetchEntriesByRange(
  from: string,
  to: string,
): Promise<Record<string, MonthlyHabitData>> {
  const data = await apiFetch(`/api/entries?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  return mapEntries(data.entries ?? []);
}

export async function fetchEntriesByYear(
  year: number,
): Promise<Record<string, MonthlyHabitData>> {
  const data = await apiFetch(`/api/entries?year=${year}`);
  return mapEntries(data.entries ?? []);
}

function mapEntries(entries: any[]): Record<string, MonthlyHabitData> {
  const result: Record<string, MonthlyHabitData> = {};
  for (const entry of entries) {
    const [y, m, d] = entry.entry_date.split('-').map(Number);
    const month = m - 1;
    const key = `${entry.habit_id}-${y}-${month}`;
    if (!result[key]) {
      result[key] = { habitId: entry.habit_id, month, year: y, entries: [] };
    }
    result[key].entries.push({
      day: d,
      ...(entry.completed != null ? { completed: entry.completed } : {}),
      ...(entry.completed_at != null ? { completedAt: entry.completed_at } : {}),
      ...(entry.rating != null ? { rating: entry.rating } : {}),
      ...(entry.text != null ? { text: entry.text } : {}),
    });
  }
  return result;
}

export async function upsertEntry(
  habitId: string,
  year: number,
  month: number,
  day: number,
  update: { completed?: boolean; completedAt?: string | null; rating?: number | null; text?: string },
): Promise<void> {
  await apiFetch('/api/entries', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ habitId, year, month, day, update }),
  });
}

// ─── Wheel of Life sections ──────────────────────────────────────────────────

export async function fetchWheelSections(): Promise<WheelSection[]> {
  const data = await apiFetch('/api/wheel-sections');
  return (data.sections ?? []).map((s: any) => ({
    id: s.id,
    name: s.name,
    color: s.color,
    rating: s.rating,
  }));
}

export async function saveWheelSections(sections: WheelSection[]): Promise<void> {
  await apiFetch('/api/wheel-sections', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections }),
  });
}

// ─── Habit PATCH (individual field update) ───────────────────────────────────

export async function patchHabit(
  habitId: string,
  fields: { name?: string; type?: HabitType; color?: string; scheduledTime?: string | null; temporary?: boolean },
): Promise<void> {
  await apiFetch(`/api/habits/${encodeURIComponent(habitId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
}

// ─── Todos ────────────────────────────────────────────────────────────────────

function mapTodo(t: any): Todo {
  return {
    id: t.id,
    title: t.title,
    completed: t.completed,
    completedAt: t.completed_at ?? null,
    onComplete: t.on_complete,
    scheduledDate: t.scheduled_date,
    scheduledTime: t.scheduled_time ?? null,
    recurrenceRule: t.recurrence_rule ?? null,
    recurrenceParentId: t.recurrence_parent_id ?? null,
    sortOrder: t.sort_order ?? 0,
    deleted: t.deleted ?? false,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  };
}

export async function fetchTodos(options: {
  includeDeleted?: boolean;
  date?: string;
  from?: string;
  to?: string;
  year?: number;
} = {}): Promise<Todo[]> {
  const params = new URLSearchParams();

  if (options.includeDeleted) params.set('includeDeleted', 'true');
  if (options.date) params.set('date', options.date);
  if (options.from) params.set('from', options.from);
  if (options.to) params.set('to', options.to);
  if (options.year != null) params.set('year', String(options.year));

  const query = params.toString() ? `?${params.toString()}` : '';
  const data = await apiFetch(`/api/todos${query}`);
  return (data.todos ?? []).map(mapTodo);
}

export async function fetchAllTodos(options: { includeDeleted?: boolean; year?: number } = {}): Promise<Todo[]> {
  return fetchTodos(options);
}

export async function upsertTodo(todo: Todo, options?: { initialOccurrenceId?: string }): Promise<void> {
  await apiFetch(`/api/todos/${encodeURIComponent(todo.id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...todo,
      ...(options?.initialOccurrenceId ? { initialOccurrenceId: options.initialOccurrenceId } : {}),
    }),
  });
}

export async function deleteTodo(id: string): Promise<void> {
  await apiFetch(`/api/todos/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function deleteTodoSeries(parentId: string): Promise<void> {
  await apiFetch(`/api/todos/series/${encodeURIComponent(parentId)}`, { method: 'DELETE' });
}

// ─── Wheel of Life notes ──────────────────────────────────────────────────────

export interface WheelNoteRow {
  id: string;
  section_id: string;
  ten_out_of_ten: string;
  created_at: string;
}

export async function fetchWheelNotes(): Promise<Record<string, WheelNoteRecord[]>> {
  const data = await apiFetch('/api/wheel-notes');
  const bySection: Record<string, WheelNoteRecord[]> = {};
  for (const r of (data.records ?? []) as WheelNoteRow[]) {
    const key = r.section_id;
    if (!bySection[key]) bySection[key] = [];
    bySection[key].push({ id: r.id, tenOutOfTen: r.ten_out_of_ten, createdAt: r.created_at });
  }
  return bySection;
}

export async function addWheelNote(sectionId: string, text: string): Promise<WheelNoteRecord> {
  const data = await apiFetch(`/api/wheel-notes/${encodeURIComponent(sectionId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  return {
    id: data.id,
    tenOutOfTen: data.tenOutOfTen ?? data.ten_out_of_ten,
    createdAt: data.createdAt ?? data.created_at,
  };
}

export async function updateWheelNote(sectionId: string, recordId: string, text: string): Promise<void> {
  await apiFetch(`/api/wheel-notes/${encodeURIComponent(sectionId)}/${encodeURIComponent(recordId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

export async function deleteWheelNote(sectionId: string, recordId: string): Promise<void> {
  await apiFetch(`/api/wheel-notes/${encodeURIComponent(sectionId)}/${encodeURIComponent(recordId)}`, {
    method: 'DELETE',
  });
}

// ─── Account ──────────────────────────────────────────────────────────────────

export interface AccountStatus {
  deletionDueAt: string | null;
}

export async function fetchAccountStatus(): Promise<AccountStatus> {
  return apiFetch('/api/account');
}

export async function deleteAccount(): Promise<AccountStatus> {
  return apiFetch('/api/account', { method: 'DELETE' });
}

export async function restoreAccount(): Promise<AccountStatus> {
  return apiFetch('/api/account/restore', { method: 'POST' });
}

// ─── User preferences ─────────────────────────────────────────────────────────

export async function getUserPreferences(): Promise<any> {
  return apiFetch('/api/user-preferences');
}

export async function updateUserPreferences(data: Record<string, unknown>): Promise<any> {
  return apiFetch('/api/user-preferences', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
